import React, { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { StandaloneSearchBox, LoadScript } from "@react-google-maps/api";
import axiosConfig from "../../Service/axiosConfig";
import { AccessDeny } from "./AccesssDeny";
import { HiOutlineLocationMarker } from "react-icons/hi";
import {
  requestGeolocation,
  checkGeolocationPermission,
  getGeolocationErrorMessage,
  GEOLOCATION_ERRORS,
  getDistanceInKm,
} from "../../Utils/geolocation";

const LocationModal = ({
  LocationpopupType,
  setLocationpopupType,
  setLocationAddress,
  Location,
  SetOnline,
  triggerLocationUpdate,
}) => {
  const DELIVERY_RADIUS_KM = 4;
  const [show, setShow] = useState(false);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [error, setError] = useState(null);
  const [key, setKey] = useState("");
  const inputRef = useRef();
  const [NoAccess, setNoAccess] = useState("");
  const [storeDetails, setStoreDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permissionState, setPermissionState] = useState("unknown");

  const HandleAccess = () => {
    setShow(false);
    setNoAccess("success");
  };

  const persistResolvedStore = (storeMessage, lat, lng, resolvedAddress) => {
    localStorage.setItem("storeId", storeMessage.store_id || storeMessage.id);
    localStorage.setItem("address", resolvedAddress || storeMessage.address || "");
    localStorage.setItem("address_id", "");
    localStorage.setItem("latitude", lat);
    localStorage.setItem("longitude", lng);
    localStorage.setItem("opens_at", storeMessage.opens_at || "");
    localStorage.setItem("closes_at", storeMessage.closes_at || "");
    localStorage.setItem("slot_duration", storeMessage.slot_duration || "");
    localStorage.setItem("schedule_delivery", storeMessage.schedule_delivery || false);

    if (storeMessage.is_online === "offline") {
      localStorage.setItem("express_delivery", false);
      localStorage.removeItem("express_delivery_time");
      localStorage.removeItem("traveltime");
      return;
    }

    localStorage.setItem("traveltime", storeMessage.travel_time || "");
    localStorage.setItem("express_delivery", !!storeMessage.express_delivery);
    if (storeMessage.express_delivery && storeMessage.express_delivery_time) {
      localStorage.setItem("express_delivery_time", storeMessage.express_delivery_time);
    } else {
      localStorage.removeItem("express_delivery_time");
    }
  };

  const findNearestServiceableStore = async (lat, lng) => {
    try {
      const response = await axiosConfig.get(
        `/stores/stores/?page=1&page_size=50&is_suspended=false`
      );
      const stores = response?.data?.results || [];
      const nearby = stores
        .filter((s) => s.latitude && s.longitude && !s.is_suspended)
        .map((s) => ({
          ...s,
          distance: getDistanceInKm(
            parseFloat(lat),
            parseFloat(lng),
            parseFloat(s.latitude),
            parseFloat(s.longitude)
          ),
        }))
        .filter((s) => s.distance <= DELIVERY_RADIUS_KM)
        .sort((a, b) => {
          if (a.is_online !== b.is_online) {
            return a.is_online === "online" ? -1 : 1;
          }
          return a.distance - b.distance;
        });

      return nearby[0] || null;
    } catch (err) {
      console.log("Nearest store lookup failed", err);
      return null;
    }
  };

  const resolveStoreForCoordinates = async (lat, lng, resolvedAddress = "") => {
    const nearestStore = await findNearestServiceableStore(lat, lng);
    if (nearestStore) {
      persistResolvedStore(
        {
          store_id: nearestStore.id,
          opens_at: nearestStore.opens_at,
          closes_at: nearestStore.closes_at,
          slot_duration: nearestStore.slot_duration,
          schedule_delivery: nearestStore.schedule_delivery,
          express_delivery: nearestStore.express_delivery,
          express_delivery_time: nearestStore.express_delivery_time,
          travel_time: nearestStore.travel_time,
          is_online: nearestStore.is_online,
          address: nearestStore.address,
        },
        lat,
        lng,
        resolvedAddress || nearestStore.address || ""
      );
      setShow(false);
      setLocationAddress(resolvedAddress || nearestStore.address || "");
      setLocationpopupType && setLocationpopupType("");
      triggerLocationUpdate && triggerLocationUpdate();
      return true;
    }

    return false;
  };

  const handlePlaceChanged = async () => {
    const [place] = inputRef.current.getPlaces();
    if (place) {
      console.log(place.formatted_address);
      console.log(place.geometry.location.lat());
      console.log(place.geometry.location.lng());
      try {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const resolved = await resolveStoreForCoordinates(
          lat,
          lng,
          place.formatted_address
        );
        if (!resolved) {
          HandleAccess();
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const GetKey = async () => {
    try {
      const responce = await axiosConfig.get(`/masters/googlemapkey/`);
      console.log(responce);
      if (responce.data?.results?.length > 0) {
        setKey(responce.data.results[0]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    GetKey();
  }, []);

  const handleError = (message) => {
    setError(message);
    console.error(message);
  };

  const fetchStoreData = async (latitude, longitude) => {
    try {
      setLoading(true);
      const resolved = await resolveStoreForCoordinates(latitude, longitude);
      if (resolved) {
        await fetchAndSaveAddress(latitude, longitude);
      } else {
        HandleAccess();
      }
    } catch (error) {
      handleError("Failed to fetch store data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAndSaveAddress = async (latitude, longitude) => {
    try {
      const geocodingResponse = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${key?.key}`
      );

      if (geocodingResponse.ok) {
        const geocodingData = await geocodingResponse.json();
        if (geocodingData.results && geocodingData.results.length > 0) {
          const address = geocodingData.results[0].formatted_address;
          localStorage.setItem("address", address);
          localStorage.setItem("latitude", latitude);
          localStorage.setItem("longitude", longitude);
          setLocationAddress(address);
        } else {
          setDefaultLocationData();
        }
      } else {
        setDefaultLocationData();
      }
    } catch (error) {
      handleError("Failed to fetch address data.");
      setDefaultLocationData();
    }
  };

  const setDefaultLocationData = () => {
    localStorage.setItem("address", "");
    localStorage.setItem("latitude", "");
    localStorage.setItem("longitude", "");
    setLocationAddress("");
  };

  // Cross-browser geolocation handler using the shared utility.
  // Replaces the old buggy getCurrentPosition call (setShow was wrongly passed
  // as the options arg). Now supports iOS Safari, Android Chrome, etc. with
  // proper permission pre-checks and user-friendly error messages.
  const handleLocationDetection = async () => {
    setError(null);
    try {
      const permission = await checkGeolocationPermission();
      setPermissionState(permission);
      if (permission === "denied") {
        handleError(getGeolocationErrorMessage(GEOLOCATION_ERRORS.PERMISSION_DENIED));
        return;
      }

      setLoading(true);
      const { latitude: lat, longitude: lng } = await requestGeolocation();
      setLatitude(lat);
      setLongitude(lng);
      await fetchStoreData(lat, lng);
    } catch (err) {
      handleError(err && err.message ? err.message : getGeolocationErrorMessage());
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (localStorage.getItem("storeId")) {
      setShow(false);
      setLocationpopupType("");
      console.log("ffgg");
    } else {
      setLocationpopupType("success");
      setShow(true);
    }
  };

  const handleShow = () => setShow(true);

  const GetData = () => {
    if (LocationpopupType === "success") {
      setShow(true);
      console.log("sss");
    } else {
      setShow(false);
      setLocationpopupType && setLocationpopupType("");
    }
  };

  useEffect(() => {
    GetData();
  }, [LocationpopupType]);

  // Pre-flight permission check on modal open so we can warn the user up-front
  // when location access has already been denied (especially iOS Safari).
  useEffect(() => {
    if (!show) {
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const state = await checkGeolocationPermission();
      if (cancelled) return;
      setPermissionState(state);
      if (state === "denied") {
        setError(getGeolocationErrorMessage(GEOLOCATION_ERRORS.PERMISSION_DENIED));
      } else if (state === "unsupported") {
        setError(getGeolocationErrorMessage(GEOLOCATION_ERRORS.NOT_SUPPORTED));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show]);

  return (
    <>
      <div>
        <Modal className="template-temp2" show={show} onHide={handleClose}>
          <Modal.Header
            closeButton={localStorage.getItem("storeId") ? true : false}
          >
            <Modal.Title className="template-temp2 change-location-title">
              {localStorage.getItem("storeId")
                ? "Change Location"
                : "Welcome to Balaji Grand Bazaar"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {key?.key ? (
              <LoadScript googleMapsApiKey={key.key} libraries={["places"]}>
                {localStorage.getItem("storeId") ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      type="button"
                      className="btn--link"
                      onClick={handleLocationDetection}
                      disabled={loading}
                    >
                      {loading ? "Detecting..." : "Detect My Location"}
                    </button>
                    <div className="oval-container">
                      <div className="oval">
                        <span className="separator-text">
                          <div className="or">OR</div>
                        </span>
                      </div>
                    </div>
                    <div>
                      <StandaloneSearchBox
                        onLoad={(ref) => (inputRef.current = ref)}
                        onPlacesChanged={handlePlaceChanged}
                      >
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Location"
                        />
                      </StandaloneSearchBox>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex gap-2">
                    <div className="desktop">
                      <HiOutlineLocationMarker className="location-modal" />
                    </div>
                    <div className="delvery--para">
                      <p className="d-flex gap-2">
                        <span className="mobile">
                          <HiOutlineLocationMarker className="location-modal" />
                        </span>
                        Please Provide your delivery location to see{" "}
                        <br className="desktop" />
                        products at nearby share
                      </p>
                      <div className="d-flex align-items-center">
                        <button
                          type="button"
                          className="btn--link"
                          onClick={handleLocationDetection}
                          disabled={loading}
                        >
                          {loading ? "Detecting..." : "Detect My Location"}
                        </button>
                        <div className="oval-container">
                          <div className="oval">
                            <span className="separator-text">
                              <div className="or">OR</div>
                            </span>
                          </div>
                        </div>
                        <div>
                          <StandaloneSearchBox
                            onLoad={(ref) => (inputRef.current = ref)}
                            onPlacesChanged={handlePlaceChanged}
                          >
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Location"
                            />
                          </StandaloneSearchBox>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </LoadScript>
            ) : (
              <div>Loading map...</div>
            )}
            {error && (
              <div className="Loginerror mt-2 text-center" role="alert">
                {error}
              </div>
            )}
          </Modal.Body>
        </Modal>
        {NoAccess === "success" && (
          <AccessDeny
            NoAccess={NoAccess}
            setNoAccess={setNoAccess}
            setLocationAddress={setLocationAddress}
          />
        )}
      </div>
    </>
  );
};
export { LocationModal };
